import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown, MapPin } from "lucide-react";

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.5 + i * 0.14 },
  }),
};

const HERO_BG = "https://images.pexels.com/photos/6872582/pexels-photo-6872582.jpeg";

export const Hero = ({ onBook, onExplore }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const lines = ["CLEAN.", "PROTECT.", "PERFECT."];

  return (
    <section ref={ref} className="relative min-h-screen overflow-hidden flex flex-col justify-end" data-testid="hero">
      {/* Parallax background */}
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img src={HERO_BG} alt="" className="h-[115%] w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-[#050505]/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/80 to-transparent" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 mx-auto max-w-[1500px] w-full px-6 md:px-12 pb-16 md:pb-24">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="flex items-center gap-3 mb-6"
        >
          <span className="h-px w-10 bg-[#0055ff]" />
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-white/70 flex items-center gap-2">
            <MapPin size={13} className="text-[#3377ff]" /> Mobile Detailing · Perth, WA
          </span>
        </motion.div>

        <h1 className="font-display font-semibold leading-[0.85] tracking-tighter text-[16vw] md:text-[11vw] lg:text-[9.5vw] text-white">
          {lines.map((line, i) => (
            <span key={line} className="block overflow-hidden">
              <motion.span custom={i} variants={lineVariants} initial="hidden" animate="show" className="block">
                {i === 2 ? (
                  <>
                    <span className="text-white/30">PER</span>
                    <span className="text-[#3377ff]">FECT.</span>
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-10 flex flex-col md:flex-row md:items-end justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="max-w-md text-white/60 text-base md:text-lg leading-relaxed"
          >
            We bring the studio to your driveway. Precision hand detailing that restores every
            vehicle to showroom condition — inside, out, and everywhere in between.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 0.8 }}
            className="flex items-center gap-4"
          >
            <button
              onClick={onBook}
              data-testid="hero-book-btn"
              className="cta-sharp bg-[#0055ff] hover:bg-[#3377ff] text-white px-8 py-4 font-mono text-xs uppercase tracking-[0.15em]"
            >
              Book a Detail
            </button>
            <button
              onClick={onExplore}
              data-testid="hero-explore-btn"
              className="cta-sharp border border-white/25 hover:border-white text-white px-8 py-4 font-mono text-xs uppercase tracking-[0.15em] flex items-center gap-2"
            >
              Explore <ArrowDown size={14} />
            </button>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};
